import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { ContentCard } from '../components/ContentCard'
import { ShareBar, TextSizeControls } from '../components/ShareBar'
import { SubscribeCTA } from '../components/SubscribeCTA'
import { WallpaperCard } from '../components/WallpaperCard'
import { YouTubeEmbed } from '../components/YouTubeEmbed'
import { getWallpaperById } from '../data/articles'
import { contentHref, getContentById, getContentBySlug } from '../data/content'
import { DEITIES } from '../data/deities'
import { SITE } from '../data/site'
import { useSeo } from '../hooks/useSeo'
import './ContentDetail.css'

export function ContentDetailPage() {
  const { slug: routeSlug = '' } = useParams()
  const { pathname } = useLocation()
  const slug =
    routeSlug ||
    (pathname.replace(/\/$/, '') === '/narayani-stuti-tamil-lyrics-meaning'
      ? 'narayani-stuti-tamil-lyrics-meaning'
      : '')
  const [searchParams, setSearchParams] = useSearchParams()
  const item = getContentBySlug(slug)
  const lyricsOnly = searchParams.get('view') === 'lyrics'
  const [textScale, setTextScale] = useState(1)

  const lyricsText = useMemo(
    () => (item ? item.verses.map((v) => v.tamil).join('\n\n') : ''),
    [item],
  )

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined' || !item) return ''
    const url = new URL(contentHref(item), window.location.origin)
    if (lyricsOnly) url.searchParams.set('view', 'lyrics')
    return url.toString()
  }, [item, lyricsOnly])

  const pagePath = item ? `${contentHref(item)}${lyricsOnly ? '?view=lyrics' : ''}` : undefined
  const pageTitle = item
    ? lyricsOnly
      ? `${item.seoTitle ?? item.titleTa} — வரிகள் மட்டும்`
      : (item.seoTitle ?? `${item.titleTa} — ${item.titleEn}`)
    : 'பக்கம் இல்லை'

  useSeo({
    title: pageTitle,
    description: item?.seoDescription,
    path: pagePath,
    videoId: item?.youtubeVideoId,
    skipTitleSuffix: Boolean(item?.seoTitleExact && !lyricsOnly),
    jsonLd:
      item?.id === 'narayani-stuti' && pagePath
        ? {
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'முகப்பு', item: `https://${SITE.domain}/` },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'அம்மன் / தேவி',
                    item: `https://${SITE.domain}/deities/amman`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: 'நாராயணி ஸ்துதி',
                    item: `https://${SITE.domain}${contentHref(item)}`,
                  },
                ],
              },
              {
                '@type': 'VideoObject',
                name: 'நவராத்திரி ஸ்பெஷல் — நாராயணி ஸ்துதி | Sarva Mangala Mangalye',
                description: item.seoDescription,
                thumbnailUrl: `https://i.ytimg.com/vi/${item.youtubeVideoId}/hqdefault.jpg`,
                embedUrl: `https://www.youtube-nocookie.com/embed/${item.youtubeVideoId}`,
                contentUrl: `https://www.youtube.com/watch?v=${item.youtubeVideoId}`,
              },
              {
                '@type': 'WebPage',
                name: item.seoTitle,
                description: item.seoDescription,
                inLanguage: 'ta',
                url: `https://${SITE.domain}${contentHref(item)}`,
              },
            ],
          }
        : undefined,
  })

  useEffect(() => {
    if (!lyricsOnly) return
    document.body.classList.add('lyrics-print-mode')
    return () => document.body.classList.remove('lyrics-print-mode')
  }, [lyricsOnly])

  function setLyricsOnly(next: boolean) {
    const params = new URLSearchParams(searchParams)
    if (next) params.set('view', 'lyrics')
    else params.delete('view')
    setSearchParams(params, { replace: true })
  }

  if (!item) {
    return (
      <div className="container section">
        <div className="empty-state">
          <h1>பக்கம் கிடைக்கவில்லை</h1>
          <Link className="btn btn--primary" to="/">
            முகப்புக்குச் செல்லவும்
          </Link>
        </div>
      </div>
    )
  }

  const deity = DEITIES.find((d) => d.id === item.deity)
  const related = (item.relatedIds ?? []).map(getContentById).filter(Boolean)
  const wallpapers = (item.wallpaperIds ?? []).map(getWallpaperById).filter(Boolean)

  return (
    <>
      <article className={`section ${lyricsOnly ? 'is-lyrics-only' : ''}`}>
        <div className="container">
          <Breadcrumbs
            items={
              item.id === 'narayani-stuti'
                ? [
                    { label: 'முகப்பு', to: '/' },
                    { label: 'அம்மன் / தேவி', to: '/deities/amman' },
                    { label: 'நாராயணி ஸ்துதி' },
                  ]
                : [
                    { label: 'முகப்பு', to: '/' },
                    {
                      label: item.type === 'original-song' ? 'பாடல்கள்' : 'ஸ்துதிகள்',
                      to: item.type === 'original-song' ? '/songs' : '/stutis',
                    },
                    { label: item.cardTitleTa ?? item.titleTa },
                  ]
            }
          />

          <header className="content-detail__header">
            <p className="eyebrow">
              {item.eyebrowTa ?? deity?.nameTa}
              {item.isOriginalDO ? ' · தெய்வீக ஓசை அசல்' : ''}
              {lyricsOnly ? ' · வரிகள் மட்டும்' : ''}
            </p>
            <h1>{item.titleTa}</h1>
            <p className="content-detail__en">{item.titleEn}</p>
            {!lyricsOnly && <p className="content-detail__intro">{item.shortIntroTa}</p>}
          </header>

          {!lyricsOnly && (
            <YouTubeEmbed
              videoId={item.youtubeVideoId}
              title={
                item.id === 'narayani-stuti'
                  ? 'Narayani Stuti Tamil - Deiveega Oosai'
                  : item.titleTa
              }
            />
          )}

          {!lyricsOnly && item.youtubeVideoId && item.id === 'narayani-stuti' && (
            <div className="btn-row" style={{ marginTop: '1rem' }}>
              <a
                className="btn btn--gold"
                href={`https://www.youtube.com/watch?v=${item.youtubeVideoId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                ▶ YouTube-ல் கேட்க
              </a>
            </div>
          )}

          {!lyricsOnly && item.pdfUrl && (
            <div className="pdf-panel">
              <p className="pdf-panel__label">Mobile-friendly PDF</p>
              <div className="btn-row">
                <a className="btn btn--outline" href={item.pdfUrl} target="_blank" rel="noopener noreferrer">
                  PDF முன்னோட்டம்
                </a>
                <a
                  className="btn btn--primary"
                  href={item.pdfUrl}
                  download
                  onClick={() => {
                    const w = window as Window & { gtag?: (...args: unknown[]) => void }
                    w.gtag?.('event', 'pdf_download', { event_label: item.id })
                  }}
                >
                  PDF பதிவிறக்கு
                </a>
              </div>
            </div>
          )}

          {item.verses.length > 0 && (
            <>
              <div className="content-toolbar">
                <TextSizeControls size={textScale} onChange={setTextScale} />
                <label className="lyrics-toggle">
                  <input
                    type="checkbox"
                    checked={lyricsOnly}
                    onChange={(e) => setLyricsOnly(e.target.checked)}
                  />
                  வரிகள் மட்டும்
                </label>
              </div>
              <p className="lyrics-toggle-hint">
                இயக்கினால் வீடியோ மற்றும் பொருள் மறைந்து வரிகள் மட்டும் காட்டும். பகிரும் லிங்க்
                (`?view=lyrics`) திறக்கும்போதும் இதே பார்வை வரும்.
              </p>
            </>
          )}

          <ShareBar title={`${item.titleTa} | ${SITE.nameTa}`} text={lyricsText} shareUrl={shareUrl} />

          {item.verses.length > 0 ? (
            <section className="lyrics" style={{ fontSize: `${textScale}rem` }}>
              <h2>{lyricsOnly ? 'வரிகள்' : 'வரிகள் & பொருள்'}</h2>
              {item.verses.map((verse, i) => (
                <div key={i} className="verse">
                  {verse.original && !lyricsOnly && (
                    <pre className="verse__sanskrit" lang="sa">
                      {verse.original}
                    </pre>
                  )}
                  <pre className="verse__tamil">{verse.tamil}</pre>
                  {!lyricsOnly && (
                    <>
                      {verse.translitEn && <p className="verse__translit">{verse.translitEn}</p>}
                      <p className="verse__meaning">{verse.meaningTa}</p>
                    </>
                  )}
                </div>
              ))}
            </section>
          ) : (
            !lyricsOnly && item.lyricsPending && (
              <section className="lyrics-pending prose">
                <h2>வரிகள்</h2>
                <p>
                  அங்கீகரிக்கப்பட்ட production master கிடைக்கும் வரை exact வரிகள்/நாமாவளி பட்டியல் இங்கே
                  வெளியிடப்படவில்லை. முழு உள்ளடக்கத்திற்கு மேலே உள்ள YouTube வீடியோவைக் கேளுங்கள்.
                </p>
              </section>
            )
          )}

          {!lyricsOnly && (
            <section className="content-meta prose">
              {item.themeTa && (
                <>
                  <h2>பாடல் கருத்து</h2>
                  <p>{item.themeTa}</p>
                </>
              )}
              {item.occasionTa && (
                <>
                  <h2>எப்போது கேட்பது / பயன்படுத்துவது</h2>
                  <p>{item.occasionTa}</p>
                </>
              )}
              {item.originTa && (
                <>
                  <h2>{item.id === 'narayani-stuti' ? 'இந்த ஸ்துதி பற்றி' : 'பின்னணி'}</h2>
                  <p>{item.originTa}</p>
                </>
              )}
              {item.isOriginalDO && (
                <p className="do-badge">இது தெய்வீக ஓசை தயாரித்த அசல் பக்திப் பாடல்.</p>
              )}
              {item.disclaimerTa && <p className="disclaimer-inline disclaimer-inline--strong">{item.disclaimerTa}</p>}
              <p className="disclaimer-inline">
                குறிப்பு: பழக்க வழக்கங்கள் பிராந்தியத்துக்கு ஏற்ப மாறலாம். எந்த உத்தரவாத முடிவும் உரிமை
                கோரப்படுவதில்லை.
              </p>
            </section>
          )}

          {!lyricsOnly && wallpapers.length > 0 && (
            <section className="section--tight">
              <h2>மொபைல் வால்பேப்பர்கள்</h2>
              <div className="card-grid card-grid--3">
                {wallpapers.map((wp) => wp && <WallpaperCard key={wp.id} item={wp} />)}
              </div>
            </section>
          )}

          {!lyricsOnly && related.length > 0 && (
            <section className="section--tight">
              <h2>{item.id === 'narayani-stuti' ? 'தொடர்புடைய பிரார்த்தனைகள்' : 'தொடர்புடையவை'}</h2>
              <div className="card-grid card-grid--3">
                {related.map((r) => r && <ContentCard key={r.id} item={r} />)}
              </div>
            </section>
          )}

          {!lyricsOnly && item.id === 'narayani-stuti' && (
            <section className="section--tight">
              <h2>நவராத்திரி சிறப்பு</h2>
              <p>நவராத்திரி சிறப்பு பாடல்கள் மற்றும் Giveaway விவரங்கள் இங்கே.</p>
              <Link className="btn btn--primary" to="/navratri/">
                நவராத்திரி சிறப்பு பாடல்கள் & Giveaway
              </Link>
            </section>
          )}

          {lyricsOnly && (
            <p className="lyrics-full-link">
              <button type="button" className="btn btn--outline" onClick={() => setLyricsOnly(false)}>
                முழுப் பக்கம் (வீடியோ & பொருள்)
              </button>
            </p>
          )}
        </div>
      </article>
      {!lyricsOnly && <SubscribeCTA />}
    </>
  )
}
